import React, { useEffect, useMemo, useState } from 'react';
import Input from 'lib/components/input.jsx';
import SearchPanel from 'src/components/search-panel.jsx';
import { LoadingIndicator } from '#lib/components/loading.jsx';
import { addClasses, cn } from '#lib/helpers/class.js';
import useDebounce from '#lib/hooks/use-debounce.js';
import useUserQueries from '#hooks/use-user-query.js';
import Combobox from '#lib/components/combobox';
import TagsTree from './components/tags-tree.jsx';
import { useTags } from '../hooks/use-tags.js';
import Button from '#lib/components/button.jsx';
import { Icon } from '@iconify-icon/react';
import { Card } from './components/card.jsx';
import { groupTags } from './helper.js';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useSearch } from '../hooks/use-search.js';

export default function TagSearch() {
    const [query, setQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchParams, setSearchParams] = useState({ query: '', user: '' });
    const [userQuery, setUserQuery] = useState('');
    const search = useDebounce(userQuery.toLocaleLowerCase(), 300);
    const { usersList, isSuccess } = useUserQueries(search);
    const totalUsers = useMemo(() => usersList, [isSuccess]);

    // Manage tags
    const [showTags, setShowTags] = useState(false); // show/hide tags in mobile version
    const { tags, isLoading, selectedTags, selectTags } = useTags();
    const groupedTags = groupTags(tags);
    // Manage search
    const {
        isLoading: searchIsLoading,
        isRefetching: searchIsRefetching,
        isError: searchIsError,
        isSuccess: searchIsSuccess,
        error,
        itineraries,
        availableTags,
        fetchNextPage
    } = useSearch({
        query: searchParams.query,
        userId: searchParams.user,
        selectedTags: selectedTags.map((s) => s.id)
    });
    const { ref, inView } = useInView();

    useEffect(() => {
        if (inView) fetchNextPage();
    }, [fetchNextPage, inView]);

    useEffect(() => {
        const user = usersList?.find((user) => user.id === selectedUserId);
        if (!user) return;
        setUserQuery(user?.firstName + ' ' + user?.lastName || '');
    }, [usersList, selectedUserId]);

    const handleChangeUser = (value) => {
        if (!value) {
            setUserQuery('');
            setSelectedUserId('');
            return;
        }
        setSelectedUserId(value.id);
        setUserQuery(value.firstName + ' ' + value.lastName);
    };

    const handleSearch = () => {
        setSearchParams((prev) => ({ ...prev, user: selectedUserId, query }));
    };

    useEffect(() => {
        if (!query) handleSearch();
    }, [query]);

    return (
        <main className="relative w-full lg:h-full pt-[117px] lg:flex">
            <div className="fixed z-50 lg:relative w-full lg:w-1/3 max-h-[80vh] lg:max-h-full mb-4 lg:mb-0 flex flex-col bg-neutral-100">
                <div
                    className={cn(
                        'transition-all lg:p-3 lg:pt-4',
                        showTags ? 'p-3 pt-4 overflow-auto' : 'h-0 lg:h-auto overflow-hidden lg:overflow-auto'
                    )}
                >
                    <div className="flex justify-between">
                        <h1 className="text-lg font-semibold">{_t.search.tags()}</h1>
                        <Button
                            size="xs"
                            onClick={() => selectTags([])}
                            className={addClasses('transition-all')}
                            disabled={!selectedTags?.length}
                            style={{
                                opacity: selectedTags.length ? 100 : 0
                            }}
                        >
                            {_t.search.remove()}
                        </Button>
                    </div>
                    {isLoading ? (
                        <div className="w-full mt-2 flex justify-center items-center flex-grow p-10">
                            <LoadingIndicator />
                        </div>
                    ) : (
                        <TagsTree
                            tags={groupedTags}
                            flattedTags={tags}
                            availableTags={availableTags}
                            selectedTags={selectedTags}
                            selectTags={selectTags}
                        />
                    )}
                </div>
                <div
                    className="lg:hidden w-full py-3 flex justify-center items-center text-neutral-700 cursor-pointer border border-neutral-200 bg-neutral-100 hover:bg-neutral-200 hover:pb-3 transition-all"
                    onClick={() => setShowTags((prev) => !prev)}
                >
                    <Icon
                        icon="material-symbols:double-arrow-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className={`${showTags ? '-' : ''}rotate-90`}
                    />
                </div>
            </div>
            <div
                className={cn(
                    'lg:hidden absolute left-0 top-40 w-full h-[100vh] bg-neutral-700 opacity-50 z-10 transition-all',
                    showTags ? 'block' : 'hidden'
                )}
            ></div>
            <div className="w-full md:h-full md:px-4 pt-4 mt-10 lg:mt-0 flex flex-col md:border-l md:border-l-neutral-200 overflow-auto">
                <SearchPanel onSubmit={handleSearch}>
                    <Input.Full
                        className="flex-grow"
                        type="search"
                        label={_t.global.freeTextSearch()}
                        value={query}
                        placeholder={_t.search.namePlaceholder()}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {totalUsers?.length > 1 && (
                        <>
                            <Combobox.Full
                                inPortal={false}
                                query={userQuery}
                                onQueryChange={(value) => {
                                    setUserQuery(value);
                                }}
                                onInputFocus={(e) => {
                                    setUserQuery(e.target.value);
                                }}
                                onChange={handleChangeUser}
                                displayValue={(opt) => {
                                    return opt.firstName + ' ' + opt.lastName;
                                }}
                                label={_t.edit.fields.assignedTo()}
                                placeholder={_t.edit.fields.assignedTo()}
                                options={usersList}
                                filterFn={() => true}
                                style={{
                                    minWidth: '200px',
                                    width: '30%'
                                }}
                                nullable={true}
                            ></Combobox.Full>
                            <input type="hidden" name="user" value={selectedUserId} />
                        </>
                    )}
                </SearchPanel>

                <div className={cn('w-full flex flex-col gap-4 pb-4')}>
                    {itineraries?.map((itinerary) => {
                        const { id, name, cover, tags: tagIds, assignedTo, ...data } = itinerary;
                        const user = totalUsers?.find((user) => user.id === assignedTo);
                        const username = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}` : '';
                        return (
                            <Link key={id} to={`../edit/${id}`}>
                                <Card
                                    key={id}
                                    id={id}
                                    name={name}
                                    cover={cover}
                                    tags={tagIds}
                                    flattedTags={tags}
                                    data={{ ...data, assignedTo: username }}
                                />
                            </Link>
                        );
                    })}
                    {searchIsSuccess && itineraries?.length === 0 ? (
                        <div className="w-full flex justify-center p-2 bg-neutral-50 border border-neutral-200 rounded-lg text-lg text-neutral-700">
                            {_t.components.table.noMore()}
                        </div>
                    ) : (
                        <div className="w-full flex justify-center items-center" ref={ref}>
                            {searchIsError ? (
                                <span>{error}</span>
                            ) : searchIsRefetching || searchIsLoading ? (
                                <LoadingIndicator />
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
